// One inbox (wave 13, w13-notify; docs/build/notify.md). Three services store notifications: accounts (games,
// creators, events), community (devlogs, bugs, replies, "your note shipped") and social (friends, messages, posts).
// This reads all three, puts them in one shape and one order, hides the kinds a player switched off (accounts already
// hides its own; community and social don't know the switches) and sends "read" back to whichever service owns each
// item. launcher/src/ui/notify.rs mirrors it.

import { api, type Service } from "./api.ts";
import { patchMe, type FullMe, type Notify } from "./pages/me-kit.ts";

export type Source = "accounts" | "community" | "social";

export interface InboxItem {
  /** unique across sources */
  key: string;
  id: string;
  source: Source;
  kind: string;
  /** unix seconds (social stores ms; normalised here) */
  at: number;
  read: boolean;
  gameId: string | null;
  refId: string | null;
  data: Record<string, unknown>;
}

/** Every kind → the switch in accounts' prefs that hides it (backend/accounts/src/account-prefs.ts KIND_SWITCH). */
export const KIND_SWITCH: Record<string, keyof Switches> = {
  game_released: "gameReleased",
  game_updated: "gameUpdated",
  creator_shipped: "creatorShipped",
  event_starting: "eventStarting",
  event_reminder: "eventReminder",
  friend_request: "friends",
  friend_accepted: "friends",
  party_invite: "friends",
  x_joined: "friends",
  friend_message: "friendMessages",
  new_follower: "friends",
  follow_release: "followReleases",
  post_reply: "replies",
  mention: "replies",
  release_draft: "gameNews",
  note_shipped: "noteShipped",
  reply: "replies",
  devlog: "gameNews",
  bug_fixed: "gameNews",
  bug_status: "gameNews",
  fork_created: "forks",
  proposal_opened: "forks",
  proposal_decided: "forks",
};

export interface Switches {
  gameReleased: boolean;
  gameUpdated: boolean;
  creatorShipped: boolean;
  eventStarting: boolean;
  eventReminder: boolean;
  friendMessages: boolean;
  friends: boolean;
  noteShipped: boolean;
  followReleases: boolean;
  replies: boolean;
  gameNews: boolean;
  forks: boolean;
}

export interface QuietHours {
  from: string;
  to: string;
}

export interface Prefs extends Switches {
  quiet: QuietHours | null;
  asked: string[];
}

export const DEFAULT_PREFS: Prefs = {
  gameReleased: true,
  gameUpdated: true,
  creatorShipped: true,
  eventStarting: true,
  eventReminder: false,
  friendMessages: false,
  friends: true,
  noteShipped: true,
  followReleases: true,
  replies: true,
  gameNews: true,
  forks: true,
  quiet: null,
  asked: [],
};

/** Prefs from /me, filled with defaults for an accounts that doesn't send the wave 13 fields yet. */
export function prefsOf(n: Partial<Prefs> | Notify | null | undefined): Prefs {
  const p = { ...DEFAULT_PREFS, ...(n as Partial<Prefs> | null | undefined) };
  if (!Array.isArray(p.asked)) p.asked = [];
  return p;
}

/** Kinds with no switch: a decision about the person's own application is always shown (founding developers). */
export const ALWAYS_SHOWN = new Set(["dev_application_decided"]);

/** Shown, given the switches? Unknown kinds aren't (describe() wouldn't have words for them either). */
export const shown = (kind: string, p: Prefs): boolean => {
  if (ALWAYS_SHOWN.has(kind)) return true;
  const sw = KIND_SWITCH[kind];
  return !!sw && p[sw];
};

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** One raw item from any service → an InboxItem (null for something unusable). */
export function normalise(source: Source, raw: Record<string, unknown>): InboxItem | null {
  const id = str(raw.id);
  const kind = str(raw.kind);
  if (!id || !kind) return null;
  let at = num(raw.createdAt) ?? num(raw.created_at) ?? 0;
  if (source === "social") at = Math.floor(at / 1000);
  const readAt = raw.readAt ?? raw.read_at ?? null;
  let data = raw.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      data = {};
    }
  }
  return {
    key: `${source}:${id}`,
    id,
    source,
    kind,
    at,
    read: readAt !== null && readAt !== undefined,
    gameId: str(raw.gameId) ?? str(raw.game_id),
    refId: str(raw.ref_id),
    data: data && typeof data === "object" && !Array.isArray(data) ? (data as Record<string, unknown>) : {},
  };
}

/** Newest first, switched-off kinds out. */
export function merge(lists: { source: Source; items: Record<string, unknown>[] }[], p: Prefs): InboxItem[] {
  const out: InboxItem[] = [];
  for (const l of lists) for (const r of l.items) {
    const n = normalise(l.source, r);
    if (n && shown(n.kind, p)) out.push(n);
  }
  return out.sort((a, b) => b.at - a.at || a.key.localeCompare(b.key));
}

export interface Inbox {
  items: InboxItem[];
  unread: number;
  /** sources that didn't answer (the inbox still shows the others) */
  missing: Source[];
}

const PATHS: Record<Source, string> = {
  accounts: "/me/notifications?limit=50",
  community: "/notifications?limit=50",
  social: "/notifications",
};

/** Reads the three inboxes. A service that's down is left out and named in `missing`. */
export async function loadInbox(p: Prefs, call: typeof api = api): Promise<Inbox> {
  const sources: Source[] = ["accounts", "community", "social"];
  const got = await Promise.all(
    sources.map((s) =>
      call<{ notifications?: Record<string, unknown>[] }>(s as Service, PATHS[s]).then(
        (r) => ({ source: s, items: Array.isArray(r?.notifications) ? r.notifications : [] }),
        () => null,
      ),
    ),
  );
  const items = merge(got.filter((g): g is { source: Source; items: Record<string, unknown>[] } => !!g), p);
  return { items, unread: items.filter((i) => !i.read).length, missing: sources.filter((_, i) => !got[i]) };
}

/** Marks items read, each at its own service; with no items, everything everywhere. */
export async function markRead(items: InboxItem[] | "all", call: typeof api = api): Promise<void> {
  if (items === "all") {
    await Promise.all((["accounts", "community", "social"] as Source[]).map((s) => call(s as Service, s === "accounts" ? "/me/notifications/read" : "/notifications/read", { body: { all: true } }).catch(() => {})));
    return;
  }
  const by = new Map<Source, string[]>();
  for (const i of items) if (!i.read) by.set(i.source, [...(by.get(i.source) ?? []), i.id]);
  await Promise.all([...by].map(([s, ids]) => call(s as Service, s === "accounts" ? "/me/notifications/read" : "/notifications/read", { body: { ids } }).catch(() => {})));
}

// ---- words ---------------------------------------------------------------------------------------------------------

export interface Said {
  line: string;
  detail: string | null;
  href: string | null;
  /** icon name for the row (bell.ts draws it) */
  icon: "calendar" | "friend" | "message" | "note" | "game" | "reply" | "fork";
}

export interface Names {
  game(id: string | null): string | null;
  user(id: string | null): string;
}

const clock = (unix: number) => new Date(unix * 1000).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

/** One item in plain words. Null for a kind or shape it doesn't know: say nothing rather than guess. */
export function describe(i: InboxItem, names: Names): Said | null {
  const d = i.data;
  const game = names.game(i.gameId);
  const gameHref = i.gameId ? `/g/${encodeURIComponent(i.gameId)}` : null;
  const who = (k = "user_id") => names.user(str(d[k]));
  switch (i.kind) {
    case "event_reminder":
    case "event_starting": {
      const title = str(d.title);
      if (!title) return null;
      const at = num(d.at);
      return i.kind === "event_reminder"
        ? { line: `${title} starts in 15 minutes`, detail: at ? `At ${clock(at)} in the Plaza.` : "In the Plaza.", href: "/events", icon: "calendar" }
        : { line: `${title} is starting`, detail: "Come along. It's in the Plaza now.", href: "/events", icon: "calendar" };
    }
    case "game_released":
      return game ? { line: `${game} is out`, detail: str(d.creatorName) ? `From ${str(d.creatorName)}. You were waiting for it.` : "You were waiting for it.", href: gameHref, icon: "game" } : null;
    case "game_updated": {
      if (!game) return null;
      const v = str(d.versionLabel);
      return { line: v ? `${game} updated to ${v}` : `${game} got an update`, detail: str(d.notes)?.slice(0, 160) ?? null, href: gameHref, icon: "game" };
    }
    case "creator_shipped": {
      const by = str(d.creatorName);
      const what = str(d.title) ?? game;
      return by && what ? { line: `${by} shipped ${what}`, detail: "A creator you follow has a new game.", href: str(d.gameId) ? `/g/${encodeURIComponent(str(d.gameId)!)}` : gameHref, icon: "game" } : null;
    }
    case "note_shipped": {
      const v = str(d.version);
      if (!v || !game) return null;
      return { line: `Your note shipped in ${/^v/i.test(v) ? v : `v${v}`}`, detail: `${game} changed because of what you said. Play it.`, href: gameHref, icon: "note" };
    }
    case "reply": {
      const title = str(d.title);
      if (!title) return null;
      return { line: d.creator === true ? `The creator replied to “${title}”` : `New reply to “${title}”`, detail: game, href: gameHref, icon: "reply" };
    }
    case "devlog":
      return game && str(d.title) ? { line: `${game}: ${str(d.title)}`, detail: "A new devlog from the creator.", href: gameHref, icon: "game" } : null;
    case "bug_fixed":
      return str(d.title) ? { line: `Fixed: ${str(d.title)}`, detail: [game, str(d.fixed_in) ? `in ${str(d.fixed_in)}` : null].filter(Boolean).join(" ") || null, href: gameHref, icon: "game" } : null;
    case "bug_status":
      return str(d.title) && str(d.status) ? { line: `${str(d.title)} is now ${str(d.status)!.replace(/_/g, " ")}`, detail: game, href: gameHref, icon: "game" } : null;
    // founding developers (accounts): {applicationId, status, note, perks, creatorIds, dashboardUrl}
    case "dev_application_decided": {
      const line = ({ approved: "You're a founding developer", waitlisted: "You're first in line for the next round", declined: "Your founding developer application: not this round" } as Record<string, string>)[str(d.status) ?? ""];
      return line ? { line, detail: str(d.note)?.slice(0, 160) ?? null, href: "/dashboard", icon: "game" } : null;
    }
    case "friend_request":
      return { line: `${who()} wants to be friends`, detail: null, href: "/friends", icon: "friend" };
    case "friend_accepted":
      return { line: `${who()} is your friend now`, detail: null, href: "/friends", icon: "friend" };
    case "party_invite":
      return { line: `${who("from")} invited you to a party`, detail: "Join them from the Banger app on your computer.", href: "/friends", icon: "friend" };
    case "x_joined":
      return { line: `${who()} joined Banger`, detail: "You follow them on X, so you follow them here too.", href: "/friends", icon: "friend" };
    case "new_follower":
      return { line: `${who()} followed you`, detail: null, href: "/friends", icon: "friend" };
    case "friend_message": {
      const n = num(d.count) ?? 1;
      return { line: n > 1 ? `${who()} sent you ${n} messages` : `${who()} messaged you`, detail: "Read and reply in the Banger app on your computer.", href: "/friends", icon: "message" };
    }
    // w13-people (social): {user_id, post_id, reply_id, excerpt}; {user_id, post_id, game_id, version}
    case "post_reply":
      return { line: `${who()} replied to your banger`, detail: str(d.excerpt), href: str(d.post_id) ? `/p/${encodeURIComponent(str(d.post_id)!)}` : "/feed", icon: "reply" };
    // BANGERS.md slice 5 (social): {user_id, post_id, reply_id, excerpt}
    case "mention":
      return { line: `${who()} mentioned you`, detail: str(d.excerpt), href: str(d.post_id) ? `/p/${encodeURIComponent(str(d.post_id)!)}` : "/feed", icon: "reply" };
    case "follow_release": {
      const title = names.game(str(d.game_id)) ?? str(d.title);
      if (!title) return null;
      const v = str(d.version);
      return { line: `${who()} released ${title}${v ? ` ${/^v/i.test(v) ? v : `v${v}`}` : ""}`, detail: "Someone you follow.", href: str(d.post_id) ? `/p/${encodeURIComponent(str(d.post_id)!)}` : "/feed", icon: "game" };
    }
    case "release_draft": {
      const v = str(d.version);
      return { line: v ? `Your release post for ${v} is ready to edit` : "Your release post is ready to edit", detail: "Post it when you're happy with it.", href: "/feed", icon: "note" };
    }
    case "fork_created":
      return str(d.title) ? { line: `${who()} forked ${str(d.title)}`, detail: null, href: str(d.href), icon: "fork" } : null;
    case "proposal_opened":
      return str(d.title) ? { line: `${who()} offered a change to ${str(d.title)}`, detail: "Look it over and merge it, or decline.", href: str(d.href), icon: "fork" } : null;
    case "proposal_decided":
      return str(d.title) ? { line: `Your change to ${str(d.title)} was ${d.action === "merge" ? "merged" : "declined"}`, detail: null, href: str(d.href), icon: "fork" } : null;
  }
  return null;
}

/** The user ids an item names (to look their names up in one go). */
export function userIds(items: InboxItem[]): string[] {
  const ids = new Set<string>();
  for (const i of items) for (const k of ["user_id", "from"]) {
    const v = i.data[k];
    if (typeof v === "string" && /^u_/.test(v)) ids.add(v);
  }
  return [...ids];
}

// ---- quiet hours and in-context asks ---------------------------------------------------------------------------

const mins = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** Inside quiet hours at local time `now`? (from 22:00 to 08:00 wraps midnight.) */
export function isQuiet(q: QuietHours | null, now = new Date()): boolean {
  if (!q) return false;
  const t = now.getHours() * 60 + now.getMinutes();
  const a = mins(q.from);
  const b = mins(q.to);
  return a < b ? t >= a && t < b : t >= a || t < b;
}

export type Ask = "eventReminder" | "friendMessages";

/** Ask now? Only when the player hasn't answered this ask before (a no is remembered). */
export const shouldAsk = (p: Prefs, ask: Ask): boolean => !p.asked.includes(ask);

/** Saves the answer: the switch, and the ask as answered. */
export async function answer(ask: Ask, yes: boolean, save: (b: Record<string, unknown>) => Promise<FullMe> = patchMe): Promise<Prefs> {
  const me = await save({ notify: { [ask]: yes, asked: [ask] } });
  return prefsOf(me.notify);
}
